import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  Inject,
  OnDestroy,
  AfterViewInit,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import '@arcgis/map-components/components/arcgis-map';
import '@arcgis/map-components/components/arcgis-feature-table';
import '@arcgis/map-components/components/arcgis-locate';
import '@arcgis/map-components/components/arcgis-expand';
import '@arcgis/map-components/components/arcgis-legend';
import '@arcgis/map-components/components/arcgis-basemap-gallery';
import '@arcgis/map-components/components/arcgis-distance-measurement-2d';
import '@arcgis/map-components/components/arcgis-layer-list';
import '@arcgis/map-components/components/arcgis-search';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import LayerSearchSource from "@arcgis/core/widgets/Search/LayerSearchSource.js";

const MIN_PANEL_HEIGHT = 80;
const MAX_PANEL_HEIGHT_FRACTION = 0.8; // 80% of the container

@Component({
  selector: 'app-map-tool',
  templateUrl: './map-tool.component.html',
  styleUrls: ['./map-tool.component.scss'],
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class MapToolComponent implements AfterViewInit, OnDestroy {

  webmapId: string;
  sitesService: string;
  featureTableOpen = false;
  searchSources: any[] = [];
  sitesLayer: FeatureLayer;
  panelHeight = 300; // px — default open height, kept in sync during drag

  @ViewChildren('expandRef') expandRefs: QueryList<ElementRef>;
  @ViewChild('tablePanel') tablePanel!: ElementRef<HTMLDivElement>;
  @ViewChild('resizeHandle') resizeHandle!: ElementRef<HTMLDivElement>;

  /** Bound listeners stored so they can be removed on destroy */
  private _dragMove!: (e: MouseEvent | TouchEvent) => void;
  private _dragEnd!: () => void;

  constructor(@Inject('environment') private environment: any) {
    this.webmapId = this.environment.webmapID;
    this.sitesService = this.environment.sitesService;
    this.sitesLayer = new FeatureLayer({
      url: this.sitesService,
      title: 'WALTERS Sites'
    });
  }

  ngAfterViewInit() {
    const search = document.querySelector('arcgis-search');
    search.allPlaceholder = "Search for places or features";
    search.sources.push(
      new LayerSearchSource({
        name: 'WALTERS Sites',
        layer: this.sitesLayer,
        outFields: ['*'],
        searchFields: ['globalid', 'name', 'site_id'],
        displayField: 'name',
        exactMatch: false,
        placeholder: 'Search sites'
      })
    );
    const table = document.querySelector('arcgis-feature-table');
    table.layer = this.sitesLayer;

    this._initResizeHandle();
  }

  ngOnDestroy(): void {
    // Safety clean-up in case the component is destroyed mid-drag
    document.removeEventListener('mousemove', this._dragMove);
    document.removeEventListener('mouseup', this._dragEnd);
    document.removeEventListener('touchmove', this._dragMove);
    document.removeEventListener('touchend', this._dragEnd);
  }

  toggleFeatureTable(): void {
    this.featureTableOpen = !this.featureTableOpen;
    // Restore last-used height when reopening
    if (this.featureTableOpen) {
      this._applyPanelHeight(this.panelHeight, false);
    } else {
      this._applyPanelHeight(0, false);
    }
  }

  private _initResizeHandle(): void {
    const handle = this.resizeHandle.nativeElement;
    const panel = this.tablePanel.nativeElement;

    this._dragMove = (e: MouseEvent | TouchEvent) => {
      const clientY = e instanceof TouchEvent ? e.touches[0].clientY : e.clientY;
      const containerBottom = panel.parentElement!.getBoundingClientRect().bottom;
      const newHeight = containerBottom - clientY;
      const maxHeight = panel.parentElement!.getBoundingClientRect().height * MAX_PANEL_HEIGHT_FRACTION;
      this.panelHeight = Math.min(maxHeight, Math.max(MIN_PANEL_HEIGHT, newHeight));
      this._applyPanelHeight(this.panelHeight, true /* suppress transition */);
    };

    this._dragEnd = () => {
      panel.classList.remove('dragging');
      document.body.classList.remove('resizing');
      document.removeEventListener('mousemove', this._dragMove);
      document.removeEventListener('mouseup', this._dragEnd);
      document.removeEventListener('touchmove', this._dragMove);
      document.removeEventListener('touchend', this._dragEnd);
    };

    handle.addEventListener('mousedown', (e: MouseEvent) => {
      e.preventDefault();
      panel.classList.add('dragging');
      document.body.classList.add('resizing');
      document.addEventListener('mousemove', this._dragMove);
      document.addEventListener('mouseup', this._dragEnd);
    });

    handle.addEventListener('touchstart', (e: TouchEvent) => {
      e.preventDefault();
      panel.classList.add('dragging');
      document.body.classList.add('resizing');
      document.addEventListener('touchmove', this._dragMove, { passive: false });
      document.addEventListener('touchend', this._dragEnd);
    }, { passive: false });
  }

  private _applyPanelHeight(height: number, suppressTransition: boolean): void {
    const panel = this.tablePanel.nativeElement;
    if (suppressTransition) {
      panel.style.transition = 'none';
    } else {
      panel.style.removeProperty('transition');
    }
    panel.style.height = `${height}px`;
  }
}
